(() => {
  'use strict';

  const ROOT_SELECTOR = '[data-kwc-root]';
  const STORAGE_PREFIX = 'kratzbaumwerft-konfiguration:';
  const KEY_PATTERN = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/;
  const moneyFormat = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const number = (value, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  };

  function colorOptions(value) {
    return String(value || '')
      .split(/\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean)
      .map((line) => {
        const [name, swatch = '#d8d4cb', image = ''] = line.split('|').map((part) => part.trim());
        return {
          name,
          swatch: /^#[0-9a-f]{6}$/i.test(swatch) ? swatch : '#d8d4cb',
          image: /^https:\/\//i.test(image) ? image : ''
        };
      })
      .filter((color) => color.name);
  }

  function create(tag, className, text) {
    const element = document.createElement(tag);
    if (className) element.className = className;
    if (text !== undefined && text !== null) element.textContent = text;
    return element;
  }

  function safeCatalog(root) {
    const source = root.querySelector('[data-kwc-catalog]');
    if (!source) throw new Error('Die Konfigurator-Daten fehlen.');
    const catalog = JSON.parse(source.textContent);
    catalog.items = Array.isArray(catalog.items)
      ? catalog.items.filter((item) => item && item.active !== false && ['single', 'quantity', 'toggle', 'cover', 'note'].includes(item.type))
      : [];
    return catalog;
  }

  function encodeState(state) {
    try {
      return btoa(unescape(encodeURIComponent(JSON.stringify(state))))
        .replaceAll('+', '-')
        .replaceAll('/', '_')
        .replaceAll('=', '');
    } catch (_) {
      return '';
    }
  }

  function decodeState(value) {
    try {
      const normalized = String(value || '').replaceAll('-', '+').replaceAll('_', '/');
      const padding = '='.repeat((4 - (normalized.length % 4)) % 4);
      return JSON.parse(decodeURIComponent(escape(atob(normalized + padding))));
    } catch (_) {
      return null;
    }
  }

  function loadTurnstile() {
    if (window.turnstile) return Promise.resolve(window.turnstile);
    if (window.__kwcTurnstilePromise) return window.__kwcTurnstilePromise;
    window.__kwcTurnstilePromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = () => reject(new Error('Die Sicherheitsprüfung konnte nicht geladen werden.'));
      document.head.appendChild(script);
    });
    return window.__kwcTurnstilePromise;
  }

  class KratzbaumKonfigurator {
    constructor(root) {
      this.root = root;
      this.catalog = safeCatalog(root);
      this.form = root.querySelector('[data-kwc-form]');
      this.panels = [...root.querySelectorAll('[data-kwc-panel]')];
      this.stepTabs = [...root.querySelectorAll('[data-kwc-step-target]')];
      this.summary = root.querySelector('[data-kwc-summary]');
      this.total = root.querySelector('[data-kwc-total]');
      this.progress = root.querySelector('[data-kwc-progress]');
      this.backButton = root.querySelector('[data-kwc-back]');
      this.nextButton = root.querySelector('[data-kwc-next]');
      this.submitButton = root.querySelector('[data-kwc-submit]');
      this.errorBox = root.querySelector('[data-kwc-error]');
      this.turnstileBox = root.querySelector('[data-kwc-turnstile]');
      this.success = root.querySelector('[data-kwc-success]');
      this.shareButton = root.querySelector('[data-kwc-share]');
      this.shareStatus = root.querySelector('[data-kwc-share-status]');
      this.presetStatus = root.querySelector('[data-kwc-preset-status]');
      this.presets = root.querySelector('.kwc__presets');
      this.advice = root.querySelector('[data-kwc-advice]');
      this.currentStep = 1;
      this.startedAt = Date.now();
      this.submissionId = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
      this.turnstileWidgetId = null;
      this.turnstileToken = '';
      this.storageKey = `${STORAGE_PREFIX}${root.id}`;
      this.state = this.initialState();
      const requestedPreset = new URLSearchParams(location.search).get('preset');
      if (requestedPreset) this.state = this.presetState(requestedPreset);
      this.renderOptions();
      this.bindEvents();
      this.refresh();
      this.setStep(1, false);
      this.root.__kwcDiagnostics = this.runSelfCheck();
    }

    defaultState() {
      const state = {};
      const singleGroups = new Map();
      const coverGroups = new Map();

      this.catalog.items.forEach((item) => {
        if (item.type === 'single' && KEY_PATTERN.test(item.group || '') && KEY_PATTERN.test(item.key || '')) {
          if (!singleGroups.has(item.group)) singleGroups.set(item.group, []);
          singleGroups.get(item.group).push(item);
        }
        if (item.type === 'cover' && KEY_PATTERN.test(item.group || '') && KEY_PATTERN.test(item.key || '')) {
          if (!coverGroups.has(item.group)) coverGroups.set(item.group, []);
          coverGroups.get(item.group).push(item);
        }
        if (item.type === 'quantity' && KEY_PATTERN.test(item.key || '')) {
          state[item.key] = clamp(Math.round(number(item.default)), number(item.min), number(item.max, 5));
        }
        if (item.type === 'toggle' && KEY_PATTERN.test(item.key || '')) state[item.key] = item.default === true;
      });

      [...singleGroups, ...coverGroups].forEach(([group, items]) => {
        state[group] = (items.find((item) => item.default) || items[0] || {}).key || '';
      });
      return state;
    }

    initialState() {
      const defaults = this.defaultState();
      const fromUrl = decodeState(new URLSearchParams(location.search).get('kwc'));
      let stored = null;
      try { stored = JSON.parse(localStorage.getItem(this.storageKey) || 'null'); } catch (_) { stored = null; }
      const candidate = fromUrl && typeof fromUrl === 'object' ? fromUrl : stored && typeof stored === 'object' ? stored : {};
      return this.sanitizeState({ ...defaults, ...candidate });
    }

    presetState(name) {
      const presets = {
        compact: { height: 'height_120', basePlate: 'round', platform30: 1, platform40: 2, platform50: 0, bowls: 1, cave: 0, hammock: 0, sisalSegments: 3, macrame: 'none', cover: 'short' },
        multi: { height: 'height_180', basePlate: 'square', platform30: 1, platform40: 2, platform50: 2, bowls: 2, cave: 1, hammock: 1, sisalSegments: 6, macrame: 'none', cover: 'short' },
        mainecoon: { height: 'height_180', basePlate: 'round', platform30: 0, platform40: 0, platform50: 3, bowls: 2, cave: 0, hammock: 0, sisalSegments: 6, macrame: 'none', cover: 'short' },
        design: { height: 'height_180', basePlate: 'treeSlice', platform30: 1, platform40: 2, platform50: 1, bowls: 1, cave: 0, hammock: 0, sisalSegments: 4, macrame: 'basketRound', cover: 'premium' }
      };
      const selected = presets[String(name || '').toLowerCase()];
      return selected ? this.sanitizeState({ ...this.defaultState(), ...selected }) : this.state || this.defaultState();
    }

    applyPreset(name) {
      const next = this.presetState(name);
      if (!next) return;
      this.state = next;
      this.persist();
      this.refresh();
      this.setStep(1);
      if (this.presetStatus) {
        this.presetStatus.textContent = 'Vorlage übernommen. Du kannst jede Auswahl verändern.';
        window.setTimeout(() => { this.presetStatus.textContent = ''; }, 4500);
      }
      document.dispatchEvent(new CustomEvent('kwc:preset', { detail: { preset: name } }));
    }

    sanitizeState(candidate) {
      const clean = this.defaultState();
      const groups = new Map();
      this.catalog.items.forEach((item) => {
        if ((item.type === 'single' || item.type === 'cover') && KEY_PATTERN.test(item.group || '')) {
          if (!groups.has(item.group)) groups.set(item.group, new Set());
          groups.get(item.group).add(item.key);
        }
        if (item.type === 'quantity' && KEY_PATTERN.test(item.key || '')) {
          clean[item.key] = clamp(Math.round(number(candidate[item.key], clean[item.key])), number(item.min), number(item.max, 5));
        }
        if (item.type === 'toggle' && KEY_PATTERN.test(item.key || '') && Object.prototype.hasOwnProperty.call(candidate, item.key)) clean[item.key] = candidate[item.key] === true;
      });
      groups.forEach((keys, group) => {
        if (keys.has(candidate[group])) clean[group] = candidate[group];
      });
      const selectedCover = this.catalog.items.find((item) => item.type === 'cover' && clean[item.group] === item.key);
      const availableColors = colorOptions(selectedCover && selectedCover.colors);
      const requestedColor = String(candidate.coverColor || '');
      clean.coverColor = availableColors.some((color) => color.name === requestedColor)
        ? requestedColor
        : (availableColors[0] || {}).name || '';
      return clean;
    }

    media(item) {
      const media = create('span', 'kwc__option-media');
      if (item.image) {
        const image = document.createElement('img');
        image.src = item.image;
        image.alt = item.imageAlt || '';
        image.loading = 'lazy';
        image.width = 450;
        image.height = 340;
        image.style.objectPosition = item.imagePosition === 'top' ? 'center top' : item.imagePosition === 'bottom' ? 'center bottom' : 'center center';
        image.style.objectFit = item.imageFit === 'contain' ? 'contain' : 'cover';
        media.appendChild(image);
      } else {
        media.classList.add('kwc__option-media--icon');
        media.textContent = item.icon || '🌿';
        media.setAttribute('aria-hidden', 'true');
      }
      return media;
    }

    priceText(price, prefix = '+ ') {
      return price > 0 ? `${prefix}${moneyFormat.format(price)}` : 'ohne Aufpreis';
    }

    badge(item) {
      if (!item.recommended) return null;
      return create('span', 'kwc__badge', 'Empfehlung');
    }

    renderOptions() {
      for (let step = 1; step <= 4; step += 1) {
        const target = this.root.querySelector(`[data-kwc-step-content="${step}"]`);
        if (!target) continue;
        const groups = new Map();
        this.catalog.items.filter((item) => item.step === step).forEach((item) => {
          if (item.type === 'single') this.renderSingle(item, target, groups);
          if (item.type === 'quantity') target.appendChild(this.renderQuantity(item));
          if (item.type === 'toggle') target.appendChild(this.renderToggle(item));
          if (item.type === 'cover') this.renderCover(item, target, groups);
          if (item.type === 'note') target.appendChild(this.renderNote(item));
        });
        if (step === 4) target.appendChild(this.renderCoverColors());
      }
    }

    renderSingle(item, target, groups) {
      const groupId = `single:${item.group}`;
      let group = groups.get(groupId);
      if (!group) {
        group = create('fieldset', `kwc__option-group kwc__option-group--${item.display || 'cards'}`);
        group.dataset.group = item.group;
        const legend = create('legend', 'kwc__group-label', item.groupLabel || 'Bitte auswählen');
        group.appendChild(legend);
        if (item.display === 'select') {
          const select = document.createElement('select');
          select.className = 'kwc__select';
          select.dataset.stateKey = item.group;
          select.setAttribute('aria-label', item.groupLabel || 'Bitte auswählen');
          group.appendChild(select);
          group._optionsTarget = select;
        } else {
          const grid = create('div', item.display === 'compact' ? 'kwc__choice-grid kwc__choice-grid--compact' : 'kwc__choice-grid');
          group.appendChild(grid);
          group._optionsTarget = grid;
        }
        target.appendChild(group);
        groups.set(groupId, group);
      }

      if (item.display === 'select') {
        const option = document.createElement('option');
        option.value = item.key;
        option.textContent = item.label;
        option.dataset.blockId = item.blockId;
        group._optionsTarget.appendChild(option);
        return;
      }

      const label = create('label', 'kwc__choice');
      label.dataset.blockId = item.blockId;
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `${this.root.id}-${item.group}`;
      input.value = item.key;
      input.dataset.stateKey = item.group;
      label.append(input, this.media(item));
      const copy = create('span', 'kwc__option-copy');
      const title = create('b', '', item.label);
      const description = create('small', '', item.description || '');
      copy.append(title);
      if (item.description) copy.append(description);
      label.appendChild(copy);
      group._optionsTarget.appendChild(label);
    }

    renderQuantity(item) {
      const card = create('div', 'kwc__counter-card');
      card.dataset.blockId = item.blockId;
      card.dataset.itemKey = item.key;
      card.appendChild(this.media(item));
      const copy = create('div', 'kwc__counter-copy');
      copy.append(create('b', '', item.label));
      if (item.description) copy.append(create('small', '', item.description));

      const counter = create('div', 'kwc__counter');
      const minus = create('button', '', '−');
      minus.type = 'button';
      minus.dataset.counter = item.key;
      minus.dataset.direction = '-1';
      minus.setAttribute('aria-label', `${item.label}: eins weniger`);
      const input = document.createElement('input');
      input.type = 'number';
      input.inputMode = 'numeric';
      input.min = item.min;
      input.max = item.max;
      input.dataset.stateKey = item.key;
      input.setAttribute('aria-label', `Anzahl ${item.label}`);
      const plus = create('button', '', '+');
      plus.type = 'button';
      plus.dataset.counter = item.key;
      plus.dataset.direction = '1';
      plus.setAttribute('aria-label', `${item.label}: eins mehr`);
      counter.append(minus, input, plus);
      card.append(copy, counter);
      return card;
    }

    renderToggle(item) {
      const row = create('div', 'kwc__toggle-row');
      row.dataset.blockId = item.blockId;
      row.dataset.itemKey = item.key;
      if (item.conditionKey) {
        row.dataset.conditionKey = item.conditionKey;
        row.dataset.conditionOperator = item.conditionOperator || 'equals';
        row.dataset.conditionValue = item.conditionValue || '';
      }
      row.appendChild(this.media(item));
      const copy = create('div', 'kwc__toggle-copy');
      copy.append(create('b', '', item.label));
      if (item.description) copy.append(create('small', '', item.description));

      const control = create('label', 'kwc__switch');
      const input = document.createElement('input');
      input.type = 'checkbox';
      input.dataset.stateKey = item.key;
      const track = create('span', 'kwc__switch-track');
      track.innerHTML = '<i></i>';
      const visibleLabel = create('span', 'kwc__switch-label', 'Nein');
      control.append(input, track, visibleLabel);
      row.append(copy, control);
      return row;
    }

    renderCover(item, target, groups) {
      const groupId = `cover:${item.group}`;
      let group = groups.get(groupId);
      if (!group) {
        group = create('fieldset', 'kwc__option-group kwc__cover-group');
        group.dataset.group = item.group;
        group.append(create('legend', 'kwc__group-label', item.groupLabel || 'Bezug auswählen'));
        group._optionsTarget = create('div', 'kwc__cover-list');
        group.appendChild(group._optionsTarget);
        target.appendChild(group);
        groups.set(groupId, group);
      }

      const label = create('label', 'kwc__cover');
      label.dataset.blockId = item.blockId;
      const input = document.createElement('input');
      input.type = 'radio';
      input.name = `${this.root.id}-${item.group}`;
      input.value = item.key;
      input.dataset.stateKey = item.group;
      label.append(input, this.media(item));
      const copy = create('span', 'kwc__option-copy');
      copy.append(create('b', '', item.label));
      if (item.description) copy.append(create('small', '', item.description));
      label.appendChild(copy);
      group._optionsTarget.appendChild(label);
    }

    renderCoverColors() {
      const group = create('fieldset', 'kwc__option-group kwc__color-group');
      group.append(create('legend', 'kwc__group-label', 'Welche Farbe wünschst du?'));
      const colors = create('div', 'kwc__color-grid');
      colors.dataset.kwcCoverColors = '';
      group.appendChild(colors);
      return group;
    }

    renderNote(item) {
      const note = create('div', 'kwc__hint');
      note.dataset.blockId = item.blockId;
      note.innerHTML = '<i aria-hidden="true">i</i>';
      const copy = create('div');
      if (item.title) copy.append(create('strong', '', item.title));
      if (item.text) copy.append(create('span', '', item.text));
      note.appendChild(copy);
      return note;
    }

    bindEvents() {
      this.root.addEventListener('change', (event) => {
        const input = event.target.closest('[data-state-key]');
        if (!input) {
          if (event.target.matches('[name="catsCount"], [name="largestCatWeight"], [name="roomSize"]')) this.updateAdvice();
          return;
        }
        const key = input.dataset.stateKey;
        if (input.type === 'checkbox') this.state[key] = input.checked;
        else if (input.type === 'number') this.state[key] = Math.round(number(input.value));
        else this.state[key] = input.value;
        this.state = this.sanitizeState(this.state);
        this.persist();
        this.refresh();
      });

      this.root.addEventListener('click', (event) => {
        const preset = event.target.closest('[data-kwc-preset]');
        if (preset) {
          this.applyPreset(preset.dataset.kwcPreset);
          return;
        }
        const counter = event.target.closest('[data-counter]');
        if (counter) {
          const key = counter.dataset.counter;
          this.state[key] = number(this.state[key]) + number(counter.dataset.direction);
          this.state = this.sanitizeState(this.state);
          this.persist();
          this.refresh();
        }
      });

      this.backButton.addEventListener('click', () => this.setStep(this.currentStep - 1));
      this.nextButton.addEventListener('click', () => {
        if (this.validateStep(this.currentStep)) this.setStep(this.currentStep + 1);
      });
      this.stepTabs.forEach((tab) => tab.addEventListener('click', () => this.setStep(number(tab.dataset.kwcStepTarget, 1))));
      this.form.addEventListener('submit', (event) => this.submit(event));
      if (this.shareButton) this.shareButton.addEventListener('click', () => this.share());
    }

    conditionMatches(item) {
      if (!item.conditionKey) return true;
      const equals = String(this.state[item.conditionKey]) === String(item.conditionValue || '');
      return item.conditionOperator === 'not_equals' ? !equals : equals;
    }

    refreshVisibility() {
      this.catalog.items.filter((item) => item.type === 'toggle').forEach((item) => {
        const visible = this.conditionMatches(item);
        const row = this.root.querySelector(`[data-item-key="${CSS.escape(item.key)}"]`);
        if (row) row.hidden = !visible;
        if (!visible && this.state[item.key]) this.state[item.key] = false;
      });
    }

    refreshInputs() {
      this.refreshCoverColors();
      this.root.querySelectorAll('[data-state-key]').forEach((input) => {
        const value = this.state[input.dataset.stateKey];
        if (input.type === 'radio') input.checked = input.value === value;
        else if (input.type === 'checkbox') input.checked = value === true;
        else input.value = value ?? '';
      });
      this.root.querySelectorAll('.kwc__switch').forEach((control) => {
        const input = control.querySelector('input');
        const label = control.querySelector('.kwc__switch-label');
        if (label) label.textContent = input && input.checked ? 'Ja' : 'Nein';
      });
    }

    refreshCoverColors() {
      const target = this.root.querySelector('[data-kwc-cover-colors]');
      if (!target) return;
      const selectedCover = this.catalog.items.find((item) => item.type === 'cover' && this.state[item.group] === item.key);
      const colors = colorOptions(selectedCover && selectedCover.colors);
      target.replaceChildren();
      if (!colors.length) {
        target.append(create('p', 'kwc__empty', 'Für diesen Bezug ist keine Farbauswahl erforderlich.'));
        return;
      }
      colors.forEach((color) => {
        const label = create('label', 'kwc__color-choice');
        const input = document.createElement('input');
        input.type = 'radio';
        input.name = `${this.root.id}-coverColor`;
        input.value = color.name;
        input.dataset.stateKey = 'coverColor';
        input.checked = this.state.coverColor === color.name;
        const swatch = create('span', 'kwc__color-swatch');
        if (color.image) {
          const preview = document.createElement('img');
          preview.src = color.image;
          preview.alt = '';
          preview.loading = 'lazy';
          preview.width = 320;
          preview.height = 220;
          swatch.appendChild(preview);
        } else {
          swatch.style.backgroundColor = color.swatch;
        }
        const name = create('span', 'kwc__color-name', color.name);
        label.append(input, swatch, name);
        target.appendChild(label);
      });
    }

    lineItems() {
      const lines = [];
      const quantities = this.catalog.items.filter((item) => item.type === 'quantity');

      this.catalog.items.forEach((item) => {
        if (item.type === 'single' && this.state[item.group] === item.key) {
          const price = number(item.price);
          if (!(item.hideZero && price === 0)) lines.push({ label: item.label, quantity: 1, total: price });
        }
        if (item.type === 'quantity') {
          const quantity = number(this.state[item.key]);
          if (quantity > 0) lines.push({ label: item.label, quantity, total: quantity * number(item.price) });
        }
        if (item.type === 'toggle' && this.state[item.key] === true && this.conditionMatches(item)) {
          lines.push({ label: item.label, quantity: 1, total: number(item.price) });
        }
      });

      const selectedCover = this.catalog.items.find((item) => item.type === 'cover' && this.state[item.group] === item.key);
      if (selectedCover) {
        let total = 0;
        let quantity = 0;
        quantities.forEach((item) => {
          const count = number(this.state[item.key]);
          if (count > 0 && item.coverTier && item.coverTier !== 'none') {
            quantity += count;
            total += count * number((selectedCover.prices || {})[item.coverTier]);
          }
        });
        const colorLabel = this.state.coverColor ? `${selectedCover.label} – ${this.state.coverColor}` : selectedCover.label;
        if (!(selectedCover.hideZero && total === 0)) lines.push({ label: colorLabel, quantity, total });
      }
      return lines.map((line) => ({ ...line, total: Math.round(line.total * 100) / 100 }));
    }

    refreshSummary() {
      const lines = this.lineItems();
      this.summary.replaceChildren();
      lines.forEach((line) => {
        const row = create('div', 'kwc__summary-row');
        const label = create('span', '', line.quantity > 1 ? `${line.quantity} × ${line.label}` : line.label);
        row.append(label);
        this.summary.appendChild(row);
      });
      if (!lines.length) this.summary.append(create('p', 'kwc__empty', 'Deine Auswahl erscheint hier.'));
    }

    refresh() {
      this.refreshVisibility();
      this.refreshInputs();
      this.refreshSummary();
      this.updateAdvice();
    }

    updateAdvice() {
      if (!this.advice) return;
      const cats = number(this.form.elements.catsCount?.value);
      const weight = String(this.form.elements.largestCatWeight?.value || '');
      const largePlatforms = number(this.state.platform50);
      const totalRestingPlaces = number(this.state.platform30) + number(this.state.platform40) + largePlatforms + number(this.state.bowls) + number(this.state.cave) + number(this.state.hammock);
      if ((weight === 'über 8 kg' || weight === 'unterschiedliche Gewichte') && largePlatforms < 1) {
        this.advice.textContent = 'Planungshinweis: Für eine große oder schwere Katze empfehlen wir mindestens eine großzügige Liegefläche. Die endgültige Konstruktion prüfen wir persönlich.';
      } else if (cats > 1 && totalRestingPlaces < cats) {
        this.advice.textContent = `Planungshinweis: Für ${cats} Katzen sind mehrere gut erreichbare Ruheplätze sinnvoll. Aktuell sind ${totalRestingPlaces} Liege- oder Rückzugsplätze ausgewählt.`;
      } else if (cats || weight) {
        this.advice.textContent = 'Danke – Katzenanzahl und Gewicht fließen in unsere persönliche Machbarkeitsprüfung ein.';
      } else {
        this.advice.textContent = 'Tipp: Mit Katzenanzahl, Gewicht und Stellfläche können wir deinen Entwurf schneller auf eine passende Anordnung prüfen.';
      }
    }

    persist() {
      try { localStorage.setItem(this.storageKey, JSON.stringify(this.state)); } catch (_) { /* private mode */ }
    }

    validateStep(step) {
      this.hideError();
      const requiredGroups = new Map();
      this.catalog.items.filter((item) => item.step === step && item.type === 'single' && item.required).forEach((item) => {
        if (!requiredGroups.has(item.group)) requiredGroups.set(item.group, item.groupLabel || 'Bitte eine Auswahl treffen.');
      });
      for (const [group, label] of requiredGroups) {
        if (!this.state[group]) {
          this.showError(`${label}: Bitte eine Auswahl treffen.`);
          return false;
        }
      }
      return true;
    }

    setStep(step, scroll = true) {
      this.currentStep = clamp(Math.round(number(step, 1)), 1, 5);
      this.panels.forEach((panel) => { panel.hidden = number(panel.dataset.kwcPanel) !== this.currentStep; });
      this.stepTabs.forEach((tab) => {
        const active = number(tab.dataset.kwcStepTarget) === this.currentStep;
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
        if (active) tab.setAttribute('aria-current', 'step'); else tab.removeAttribute('aria-current');
      });
      this.progress.style.width = `${this.currentStep * 20}%`;
      this.progress.parentElement?.setAttribute('aria-valuenow', String(this.currentStep));
      this.backButton.hidden = this.currentStep === 1;
      this.nextButton.hidden = this.currentStep === 5;
      this.submitButton.hidden = this.currentStep !== 5;
      if (this.presets) this.presets.hidden = this.currentStep !== 1;
      if (this.currentStep < 5) {
        const nextLabel = (this.catalog.labels.steps || [])[this.currentStep] || 'weiter';
        this.nextButton.textContent = `Weiter zu ${nextLabel}`;
      }
      if (this.currentStep === 5) this.ensureTurnstile();
      this.hideError();
      document.dispatchEvent(new CustomEvent('kwc:step', { detail: { step: this.currentStep } }));
      if (scroll) {
        this.root.querySelector('.kwc__main').scrollIntoView({ behavior: 'smooth', block: 'start' });
        this.panels.find((panel) => number(panel.dataset.kwcPanel) === this.currentStep)?.focus({ preventScroll: true });
      }
    }

    async ensureTurnstile() {
      if (!this.catalog.turnstileSiteKey || this.turnstileWidgetId !== null) return;
      try {
        const turnstile = await loadTurnstile();
        this.turnstileWidgetId = turnstile.render(this.turnstileBox, {
          sitekey: this.catalog.turnstileSiteKey,
          theme: 'light',
          size: 'flexible',
          callback: (token) => { this.turnstileToken = token; this.hideError(); },
          'expired-callback': () => { this.turnstileToken = ''; },
          'error-callback': () => { this.turnstileToken = ''; }
        });
      } catch (error) {
        this.showError(error.message);
      }
    }

    showError(message) {
      this.errorBox.textContent = message;
      this.errorBox.hidden = false;
      this.errorBox.focus?.();
      document.dispatchEvent(new CustomEvent('kwc:error', { detail: { message: String(message || '') } }));
    }

    hideError() {
      this.errorBox.hidden = true;
      this.errorBox.textContent = '';
    }

    formDetails(formData) {
      const structuredPlanning = [
        formData.get('catsCount') ? `Katzenanzahl: ${String(formData.get('catsCount'))}` : '',
        formData.get('largestCatWeight') ? `Gewicht größte Katze: ${String(formData.get('largestCatWeight'))}` : '',
        formData.get('roomSize') ? `Stellfläche / Raummaße: ${String(formData.get('roomSize'))}` : '',
        String(formData.get('planningDetails') || '')
      ].filter(Boolean).join('\n');
      return {
        planningDetails: structuredPlanning,
        postal: String(formData.get('postal') || ''),
        delivery: String(formData.get('delivery') || ''),
        phone: String(formData.get('phone') || ''),
        preferredContact: String(formData.get('preferredContact') || '')
      };
    }

    runSelfCheck() {
      const original = { ...this.state };
      const names = ['compact', 'multi', 'mainecoon', 'design'];
      const results = [];
      names.forEach((name) => {
        this.state = this.presetState(name);
        const lines = this.lineItems();
        const total = lines.reduce((sum, line) => sum + number(line.total), 0);
        results.push({ name, valid: lines.length > 0 && Number.isFinite(total) && total >= 0, lineCount: lines.length, total });
      });
      this.state = original;
      const currentLines = this.lineItems();
      const currentTotal = currentLines.reduce((sum, line) => sum + number(line.total), 0);
      results.push({ name: 'current', valid: currentLines.length > 0 && Number.isFinite(currentTotal) && currentTotal >= 0, lineCount: currentLines.length, total: currentTotal });
      this.root.dataset.kwcSelfCheck = results.every((entry) => entry.valid) ? 'passed' : 'failed';
      return { catalogVersion: this.catalog.catalogVersion, checkedAt: new Date().toISOString(), results };
    }

    async submit(event) {
      event.preventDefault();
      this.hideError();
      if (!this.form.reportValidity()) return;
      if (!this.catalog.apiEndpoint || !/^https:\/\//.test(this.catalog.apiEndpoint)) {
        this.showError('Der E-Mail-Versand ist noch nicht eingerichtet. Bitte kontaktiere uns direkt über info@kratzbaumwerft.de.');
        return;
      }
      if (this.catalog.turnstileSiteKey && !this.turnstileToken) {
        this.showError('Bitte warte kurz, bis die Sicherheitsprüfung bereit ist, und bestätige sie anschließend.');
        this.ensureTurnstile();
        return;
      }

      const formData = new FormData(this.form);
      const payload = {
        submissionId: this.submissionId,
        startedAt: this.startedAt,
        catalogVersion: this.catalog.catalogVersion,
        configuration: this.state,
        contact: {
          firstName: String(formData.get('firstName') || ''),
          email: String(formData.get('email') || '')
        },
        couponCode: String(formData.get('couponCode') || '').trim().toUpperCase().replace(/\s+/g, ''),
        newsletterConsent: formData.get('newsletterConsent') === 'on',
        newsletterConsentAt: formData.get('newsletterConsent') === 'on' ? new Date().toISOString() : '',
        details: this.formDetails(formData),
        consent: formData.get('consent') === 'on',
        company: String(formData.get('company') || ''),
        turnstileToken: this.turnstileToken
      };

      this.submitButton.disabled = true;
      this.submitButton.classList.add('is-loading');
      try {
        let result = {};
        try {
          const response = await fetch(this.catalog.apiEndpoint, {
            method: 'POST',
            headers: { 'content-type': 'text/plain;charset=UTF-8' },
            body: JSON.stringify(payload)
          });
          result = await response.json().catch(() => ({}));
          if (!response.ok || !result.ok) throw new Error(result.error || 'Webhook nicht erreichbar');
        } catch (webhookError) {
          const internalLines = this.lineItems().map((line) =>
            `${line.quantity} × ${line.label}: ${moneyFormat.format(line.total)}`
          );
          const nativeBody = [
            'Neue Naturkratzbaum-Konfiguration (Shopify-Notfallübermittlung)',
            `Vorgangs-ID: ${payload.submissionId}`,
            `Katalogstand: ${payload.catalogVersion}`,
            '',
            'Auswahl:',
            ...internalLines,
            '',
            `Konfigurationsdaten: ${JSON.stringify(payload.configuration)}`,
            `Gutscheincode zur manuellen Prüfung: ${payload.couponCode || '—'}`,
            `Planung / Sonderwünsche: ${payload.details.planningDetails || '—'}`,
            `PLZ: ${payload.details.postal || '—'}`,
            `Lieferung: ${payload.details.delivery || '—'}`,
            `Telefon: ${payload.details.phone || '—'}`,
            `Bevorzugter Kontakt: ${payload.details.preferredContact || '—'}`
          ].join('\n');
          const nativeForm = new URLSearchParams({
            form_type: 'contact',
            utf8: '✓',
            'contact[name]': payload.contact.firstName,
            'contact[email]': payload.contact.email,
            'contact[body]': nativeBody
          });
          const nativeResponse = await fetch('/contact', {
            method: 'POST',
            headers: { 'content-type': 'application/x-www-form-urlencoded;charset=UTF-8' },
            body: nativeForm.toString()
          });
          if (!nativeResponse.ok) throw webhookError;
          result = { ok: true, offerNumber: '', fallback: true };
        }
        if (payload.newsletterConsent) {
          try {
            const newsletterBody = new URLSearchParams();
            newsletterBody.set('form_type', 'customer');
            newsletterBody.set('utf8', '✓');
            newsletterBody.set('contact[email]', payload.contact.email);
            newsletterBody.set('contact[first_name]', payload.contact.firstName);
            newsletterBody.set('contact[tags]', 'newsletter, konfigurator');
            const newsletterResponse = await fetch('/contact#contact_form', {
              method: 'POST',
              headers: { 'content-type': 'application/x-www-form-urlencoded;charset=UTF-8' },
              credentials: 'same-origin',
              body: newsletterBody.toString()
            });
            if (!newsletterResponse.ok) throw new Error('Newsletter-Anmeldung nicht erreichbar');
          } catch (newsletterError) {
            console.warn('Newsletter-Anmeldung konnte nicht übermittelt werden.', newsletterError);
          }
        }

        this.form.hidden = true;
        const emailRow = this.root.querySelector('[data-kwc-success-email-row]');
        const numberRow = this.root.querySelector('[data-kwc-success-number-row]');
        const fallbackNote = this.root.querySelector('[data-kwc-fallback-note]');
        const offerNumber = String(result.offerNumber || '').trim();
        this.root.querySelector('[data-kwc-success-email]').textContent = payload.contact.email;
        this.root.querySelector('[data-kwc-success-number]').textContent = offerNumber;
        const newsletterSuccess = this.root.querySelector('[data-kwc-success-newsletter]');
        if (newsletterSuccess) newsletterSuccess.hidden = !payload.newsletterConsent;
        if (emailRow) emailRow.hidden = result.fallback === true;
        if (numberRow) numberRow.hidden = !offerNumber;
        if (fallbackNote) fallbackNote.hidden = result.fallback !== true;
        this.success.hidden = false;
        this.success.focus();
        try { localStorage.removeItem(this.storageKey); } catch (_) { /* private mode */ }
        document.dispatchEvent(new CustomEvent('kwc:submitted', { detail: { offerNumber, fallback: result.fallback === true, catalogVersion: this.catalog.catalogVersion } }));
      } catch (error) {
        this.showError(error.message || 'Die Anfrage konnte gerade nicht versendet werden.');
        if (window.turnstile && this.turnstileWidgetId !== null) window.turnstile.reset(this.turnstileWidgetId);
        this.turnstileToken = '';
      } finally {
        this.submitButton.disabled = false;
        this.submitButton.classList.remove('is-loading');
      }
    }

    async share() {
      const url = new URL(location.href);
      url.searchParams.set('kwc', encodeState(this.state));
      try {
        await navigator.clipboard.writeText(url.toString());
        this.shareStatus.textContent = 'Link kopiert';
      } catch (_) {
        window.prompt('Diesen Link kopieren:', url.toString());
        this.shareStatus.textContent = 'Link erstellt';
      }
      window.setTimeout(() => { this.shareStatus.textContent = ''; }, 3000);
    }

    selectThemeBlock(blockId) {
      const element = this.root.querySelector(`[data-block-id="${CSS.escape(blockId)}"]`);
      if (!element) return;
      const panel = element.closest('[data-kwc-panel]');
      if (panel) this.setStep(number(panel.dataset.kwcPanel), false);
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      element.classList.add('kwc__editor-highlight');
      window.setTimeout(() => element.classList.remove('kwc__editor-highlight'), 1800);
    }
  }

  function initialize(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      if (root.dataset.kwcReady === 'true') return;
      try {
        root.dataset.kwcReady = 'true';
        root.__kwc = new KratzbaumKonfigurator(root);
      } catch (error) {
        root.dataset.kwcReady = 'error';
        root.insertAdjacentHTML('afterbegin', `<p class="kwc__fatal">${String(error.message || error)}</p>`);
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initialize());
  else initialize();

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => root.__kwc?.selectThemeBlock(event.detail.blockId));
  });
})();
